import { useEffect, useState, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  SearchOutlined,
  EnvironmentOutlined,
  ShopOutlined,
  LoadingOutlined,
  StarFilled,
  EyeOutlined,
} from '@ant-design/icons';
import Layout from './components/Layout';
import BlSelect from './components/BlSelect';
import { supabase } from '../supabaseClient';
import { governorates, getInitials, parseCategories, professionTypeOptions, professionTypeLabel } from '../hooks';
import ProProfileModal from './ProProfileModal';
import './HeroSearch.css';
import './SearchResults.css';

// Normalise une chaîne : minuscules + suppression des accents (é→e, ç→c, à→a…)
function fold(s) {
  return (s || '')
    .toString()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

// Note d'une review : score_global, sinon moyenne des 5 sous-critères
function reviewScore(r) {
  if (r.score_global != null) return Number(r.score_global);
  return (
    Number(r.qualite_travail || 0) +
    Number(r.respect_delais || 0) +
    Number(r.communication || 0) +
    Number(r.rapport_qualite_prix || 0) +
    Number(r.proprete_chantier || 0)
  ) / 5;
}

// Affiche 5 étoiles, remplies selon la note moyenne
function Stars({ value }) {
  const rounded = Math.round(value || 0);
  return (
    <span className="bl-pro-stars">
      {[1, 2, 3, 4, 5].map((i) => (
        <StarFilled key={i} className={i <= rounded ? 'star-on' : 'star-off'} />
      ))}
    </span>
  );
}

function SearchResults() {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const [q, setQ] = useState(params.get('q') || '');
  const [region, setRegion] = useState(params.get('region') || '');
  const [profession, setProfession] = useState(params.get('profession') || '');

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [professions, setProfessions] = useState([]);
  const [modalPro, setModalPro] = useState(null);
  const [visibleCount, setVisibleCount] = useState(16);

  // Charge la liste des métiers (profession) distincts pour le menu "Tous types"
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('profiles')
        .select('profession')
        .eq('role', 'professional')
        .eq('is_activated', true);
      const uniq = [...new Set((data || []).map((r) => r.profession).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b));
      setProfessions(uniq);
    })();
  }, []);

  const runSearch = useCallback(async () => {
    setLoading(true);
    setError(null);
    setVisibleCount(16);
    try {
      let query = supabase
        .from('profiles')
        .select('id, firstname, lastname, profession, governorate, imageavatar, categories, phone, is_activated')
        .eq('role', 'professional')
        .eq('is_activated', true);

      if (region) query = query.eq('governorate', region);
      if (profession) query = query.ilike('profession', `%${profession}%`);

      query = query.limit(200);

      const { data, error: qErr } = await query;
      if (qErr) throw qErr;

      let profiles = Array.isArray(data) ? data : [];

      // Recherche texte : insensible à la casse ET aux accents (maçon = macon,
      // électricien = electricien…). Filtrée côté client sur nom/prénom/métier/catégories.
      const needle = fold(q);
      if (needle) {
        profiles = profiles.filter((p) => {
          const cats = parseCategories(p.categories).join(' ');
          const haystack = fold(
            `${p.firstname || ''} ${p.lastname || ''} ${p.profession || ''} ${cats}`
          );
          return haystack.includes(needle);
        });
      }

      // Note moyenne depuis pro_reviews (par target_user_id)
      const ratingById = {};
      const ids = profiles.map((p) => p.id);
      if (ids.length) {
        const { data: revs, error: rErr } = await supabase
          .from('pro_reviews')
          .select('target_user_id, score_global, qualite_travail, respect_delais, communication, rapport_qualite_prix, proprete_chantier')
          .in('target_user_id', ids);
        if (rErr) console.warn('[pro_reviews]', rErr.message);

        const byUser = {};
        (revs || []).forEach((r) => {
          if (!r.target_user_id) return;
          if (!byUser[r.target_user_id]) byUser[r.target_user_id] = [];
          byUser[r.target_user_id].push(reviewScore(r));
        });
        Object.keys(byUser).forEach((uid) => {
          const valid = byUser[uid].filter((s) => !isNaN(s) && s > 0);
          ratingById[uid] = valid.length ? valid.reduce((a, b) => a + b, 0) / valid.length : 0;
        });
      }

      setResults(profiles.map((p) => ({ ...p, _rating: ratingById[p.id] || 0, _count: 0 })));
    } catch (err) {
      console.error('[SearchResults]', err);
      setError(err.message || 'Erreur lors de la recherche');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [q, region, profession]);

  // Recherche au montage et quand l'URL change
  useEffect(() => {
    runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const submit = (e) => {
    if (e) e.preventDefault();
    const p = new URLSearchParams();
    if (q.trim()) p.set('q', q.trim());
    if (region) p.set('region', region);
    if (profession) p.set('profession', profession);
    history.push(`/recherche?${p.toString()}`);
    runSearch();
  };

  return (
    <Layout>
      <div className="bl-results-page">
        <div className="container">
          {/* Barre de recherche (réutilisée) */}
          <form className="bl-search bl-search--page" onSubmit={submit}>
            <div className="bl-search-field bl-search-text">
              <ShopOutlined className="bl-search-ic" />
              <input
                type="search"
                placeholder="Maçon, électricien, plombier.."
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <span className="bl-search-div" />
            <div className="bl-search-field">
              <BlSelect
                ariaLabel="Région"
                icon={<EnvironmentOutlined />}
                placeholder="Toutes régions"
                value={region}
                onChange={setRegion}
                options={governorates}
              />
            </div>
            <span className="bl-search-div" />
            <div className="bl-search-field">
              <BlSelect
                ariaLabel="Type"
                icon={<SearchOutlined />}
                placeholder="Tous types"
                value={profession}
                onChange={setProfession}
                options={professionTypeOptions(professions)}
              />
            </div>
            <button type="submit" className="bl-search-btn"><SearchOutlined /> Rechercher</button>
          </form>

          {/* En-tête résultats */}
          <div className="bl-results-head">
            <h1 className="bl-results-title">
              {profession || q ? `Résultats pour « ${profession ? professionTypeLabel(profession) : q} »` : 'Tous les professionnels'}
              {region ? ` à ${region}` : ''}
            </h1>
            {!loading && !error && (
              <p className="bl-results-count">{results.length} professionnel(s) trouvé(s)</p>
            )}
          </div>

          {/* États */}
          {loading && (
            <div className="bl-results-state">
              <LoadingOutlined spin style={{ fontSize: 32, color: '#fdd119' }} />
              <p>Recherche en cours…</p>
            </div>
          )}

          {!loading && error && (
            <div className="bl-results-state bl-results-error">
              <p>Une erreur est survenue : {error}</p>
              <p className="bl-results-hint">
                Si l'erreur concerne les permissions, il faut autoriser la lecture publique
                de la table <code>profiles</code> (RLS).
              </p>
            </div>
          )}

          {!loading && !error && results.length === 0 && (
            <div className="bl-results-state">
              <p>Aucun professionnel trouvé pour ces critères.</p>
              <p className="bl-results-hint">Essayez d'élargir votre recherche.</p>
            </div>
          )}

          {/* Grille de résultats */}
          {!loading && !error && results.length > 0 && (
            <div className="bl-results-grid">
              {results.slice(0, visibleCount).map((pro) => {
                const name = `${pro.firstname || ''} ${pro.lastname || ''}`.trim() || 'Professionnel';
                const initials = getInitials(pro.firstname, pro.lastname);
                const rating = pro._rating;
                const cats = parseCategories(pro.categories);
                return (
                  <div key={pro.id} className="bl-pro-card">
                    <div className="bl-pro-avatar-wrap">
                      {pro.imageavatar ? (
                        <img src={pro.imageavatar} alt={name} className="bl-pro-avatar" loading="lazy" />
                      ) : (
                        <div className="bl-pro-avatar bl-pro-avatar--initials">{initials}</div>
                      )}
                    </div>

                    <h3 className="bl-pro-name">{name}</h3>

                    {cats.length > 0 && (
                      <div className="bl-pro-cats">
                        {cats.slice(0, 3).map((c, i) => (
                          <span key={i} className="bl-pro-cat">{c}</span>
                        ))}
                      </div>
                    )}

                    {pro.governorate && (
                      <p className="bl-pro-loc">
                        <EnvironmentOutlined /> {pro.governorate}
                      </p>
                    )}

                    {rating > 0 && (
                      <div className="bl-pro-rating">
                        <Stars value={rating} />
                        <span className="bl-pro-rating-val">{rating.toFixed(1)}</span>
                      </div>
                    )}

                    <div className="bl-pro-actions">
                      <button
                        type="button"
                        className="bl-pro-btn bl-pro-btn--ghost"
                        onClick={() => setModalPro(pro)}
                      >
                        <EyeOutlined /> Profil
                      </button>
                      
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!loading && !error && results.length > visibleCount && (
            <div className="bl-results-more">
              <button
                type="button"
                className="bl-results-more-btn"
                onClick={() => setVisibleCount((c) => c + 16)}
              >
                Voir plus ({results.length - visibleCount})
              </button>
            </div>
          )}
        </div>
      </div>

      <ProProfileModal
        open={!!modalPro}
        pro={modalPro}
        onClose={() => setModalPro(null)}
      />
    </Layout>
  );
}

export default SearchResults;
